import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileClock, Landmark, Loader2, ReceiptText, type LucideIcon } from "lucide-react";
import { toast } from "sonner";

import { orpc } from "@/client";
import type { ChosenPerson } from "@/components/medico/person-search";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { OverlayShell } from "@/components/ui/overlay";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import { format } from "@/lib/format";
import type { BankMovement, BankResolution } from "@/types/financeiro";

import { BankMovementContext } from "./bank-movement-context";
import type { FinanceModal } from "./types";

function bankMethod(movement: BankMovement) {
	return movement.method?.toLocaleLowerCase("pt-BR").includes("pix") === true
		? ("pix" as const)
		: ("transfer" as const);
}

function suggestedPerson(movement: BankMovement): ChosenPerson | null {
	if (movement.suggested_person_id === null || movement.suggested_person_name === null) {
		return null;
	}

	if (movement.suggested_person_link === "patient_confirmed") {
		return {
			kind: "patient",
			patientId: movement.suggested_person_id,
			sendpulseId: movement.suggested_person_sendpulse_id,
			name: movement.suggested_person_name,
		};
	}

	return {
		kind: "lead",
		leadId: movement.suggested_person_id,
		sendpulseId: movement.suggested_person_sendpulse_id,
		name: movement.suggested_person_name,
	};
}

function Candidate({
	icon: Icon,
	title,
	detail,
	amountCents,
	disabled,
	onChoose,
}: {
	icon: LucideIcon;
	title: string;
	detail: string;
	amountCents: number;
	disabled: boolean;
	onChoose: () => void;
}) {
	return (
		<div className="flex items-center gap-3 rounded-lg px-2 py-3 hover:bg-secondary/50">
			<div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
				<Icon className="size-4" />
			</div>
			<div className="min-w-0 flex-1">
				<p className="break-words text-xs font-semibold text-foreground">{title}</p>
				<p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>
			</div>
			<p className="shrink-0 font-mono text-xs font-semibold text-foreground">
				{FinanceiroLabels.money(amountCents)}
			</p>
			<Button size="sm" variant="outline" disabled={disabled} onClick={onChoose}>
				{disabled && <Loader2 className="animate-spin" />}
				{disabled && "Vinculando"}
				{!disabled && "Vincular"}
			</Button>
		</div>
	);
}

function ResolutionContent({
	resolution,
	isPending,
	onReceipt,
	onBill,
	onSettlement,
	onCreate,
}: {
	resolution: BankResolution;
	isPending: boolean;
	onReceipt: (id: number) => void;
	onBill: (id: number) => void;
	onSettlement: (id: string) => void;
	onCreate: () => void;
}) {
	if (resolution.kind === "settlement") {
		return (
			<>
				<p className="px-2 pb-2 text-xs text-muted-foreground">
					Este depósito corresponde a um repasse da Rede. Escolha o repasse para confirmar a
					entrada.
				</p>
				{resolution.settlements.map((settlement) => (
					<Candidate
						key={settlement.external_id}
						icon={Landmark}
						title={`Repasse ${settlement.external_id}`}
						detail={`${format.date(settlement.settled_on)}, ${settlement.type}`}
						amountCents={settlement.net_cents}
						disabled={isPending}
						onChoose={() => onSettlement(settlement.external_id)}
					/>
				))}
			</>
		);
	}

	if (resolution.kind === "receipt" && resolution.receipts.length > 0) {
		return (
			<>
				<p className="px-2 pb-2 text-xs text-muted-foreground">
					Já existe um recebimento compatível. Vincule a entrada para evitar duplicidade.
				</p>
				{resolution.receipts.map((receipt) => (
					<Candidate
						key={receipt.id}
						icon={ReceiptText}
						title={receipt.person_name}
						detail={`${format.date(receipt.received_on)}, ${FinanceiroLabels.receipt.method[receipt.method]}`}
						amountCents={receipt.amount_cents}
						disabled={isPending}
						onChoose={() => onReceipt(receipt.id)}
					/>
				))}
				<div className="mt-2 flex justify-end border-t border-border px-2 pt-2">
					<Button variant="ghost" size="sm" disabled={isPending} onClick={onCreate}>
						Nenhum destes, registrar recebimento
					</Button>
				</div>
			</>
		);
	}

	if (resolution.kind === "bill" && resolution.bills.length > 0) {
		return (
			<>
				<p className="px-2 pb-2 text-xs text-muted-foreground">
					Já existe uma conta compatível. Vincule a saída para evitar duplicidade.
				</p>
				{resolution.bills.map((bill) => (
					<Candidate
						key={bill.id}
						icon={FileClock}
						title={bill.description}
						detail={`Vencimento em ${format.date(bill.due_on)}`}
						amountCents={bill.amount_cents}
						disabled={isPending}
						onChoose={() => onBill(bill.id)}
					/>
				))}
				<div className="mt-2 flex justify-end border-t border-border px-2 pt-2">
					<Button variant="ghost" size="sm" disabled={isPending} onClick={onCreate}>
						Nenhuma destas, registrar conta
					</Button>
				</div>
			</>
		);
	}

	return (
		<div className="rounded-lg border border-dashed border-border p-4">
			<p className="text-sm font-semibold text-foreground">
				{resolution.kind === "receipt" && "Nenhum recebimento compatível"}
				{resolution.kind === "bill" && "Nenhuma conta compatível"}
			</p>
			<p className="mt-1 text-xs text-muted-foreground">
				{resolution.kind === "receipt" &&
					"Registre o recebimento usando os dados desta entrada bancária."}
				{resolution.kind === "bill" &&
					"Registre a conta paga usando os dados desta saída bancária."}
			</p>
			<Button className="mt-3" size="sm" disabled={isPending} onClick={onCreate}>
				{resolution.kind === "receipt" && "Registrar recebimento"}
				{resolution.kind === "bill" && "Registrar conta paga"}
			</Button>
		</div>
	);
}

function openCreate(resolution: BankResolution, onModal: (modal: FinanceModal) => void) {
	if (resolution.kind === "receipt") {
		onModal({
			kind: "receipt",
			initial: {
				person: suggestedPerson(resolution.transaction),
				method: bankMethod(resolution.transaction),
				amount_cents: resolution.transaction.amount_cents,
				received_on: resolution.transaction.effective_on,
				description: resolution.transaction.description,
				bank: resolution.transaction,
			},
		});
		return;
	}

	if (resolution.kind === "bill") {
		onModal({
			kind: "bill",
			initial: {
				description: resolution.transaction.description,
				amount_cents: resolution.transaction.amount_cents,
				due_on: resolution.transaction.effective_on,
				bank: resolution.transaction,
			},
		});
	}
}

function ResolutionPanel({
	data,
	isLoading,
	isError,
	isPending,
	onReceipt,
	onBill,
	onSettlement,
	onModal,
}: {
	data: BankResolution | undefined;
	isLoading: boolean;
	isError: boolean;
	isPending: boolean;
	onReceipt: (id: number, bankId: number) => void;
	onBill: (id: number, bankId: number) => void;
	onSettlement: (id: string, bankId: number) => void;
	onModal: (modal: FinanceModal) => void;
}) {
	if (isLoading) {
		return (
			<div className="flex h-40 items-center justify-center text-muted-foreground">
				<Loader2 className="animate-spin" />
			</div>
		);
	}

	if (isError || data === undefined) {
		return (
			<EmptyState
				icon={Landmark}
				title="Não foi possível abrir este movimento"
				description="Ele pode já ter sido resolvido. Atualize o extrato e tente novamente."
			/>
		);
	}

	return (
		<>
			<div className="border-b border-border p-5">
				<h2 className="type-section-title text-foreground">
					{data.transaction.direction === "income" && "Resolver entrada bancária"}
					{data.transaction.direction === "expense" && "Resolver saída bancária"}
				</h2>
				<p className="mt-1 text-xs text-muted-foreground">
					Vincule a um registro existente ou crie um somente quando não houver correspondência.
				</p>
				<div className="mt-4">
					<BankMovementContext movement={data.transaction} />
				</div>
			</div>
			<div className="overflow-y-auto p-3">
				<ResolutionContent
					resolution={data}
					isPending={isPending}
					onReceipt={(receiptId) => onReceipt(receiptId, data.transaction.id)}
					onBill={(billId) => onBill(billId, data.transaction.id)}
					onSettlement={(settlementId) => onSettlement(settlementId, data.transaction.id)}
					onCreate={() => openCreate(data, onModal)}
				/>
			</div>
		</>
	);
}

export function BankResolutionModal({
	id,
	onModal,
	onClose,
}: {
	id: number;
	onModal: (modal: FinanceModal) => void;
	onClose: () => void;
}) {
	const queryClient = useQueryClient();
	const resolution = useQuery(orpc.financeiro.bank.resolution.queryOptions({ input: { id } }));
	const linked = async () => {
		await queryClient.invalidateQueries({ queryKey: orpc.financeiro.key() });
		toast.success("Movimento bancário resolvido");
		onClose();
	};
	const stale = () => toast.error("Esse movimento já foi resolvido ou não está mais disponível");
	const receipt = useMutation(
		orpc.financeiro.receipts.linkBank.mutationOptions({
			onSuccess: linked,
			onError: stale,
		}),
	);
	const bill = useMutation(
		orpc.financeiro.bills.linkBank.mutationOptions({
			onSuccess: linked,
			onError: stale,
		}),
	);
	const settlement = useMutation(
		orpc.financeiro.reconciliation.linkSettlement.mutationOptions({
			onSuccess: linked,
			onError: stale,
		}),
	);
	const isPending = receipt.isPending || bill.isPending || settlement.isPending;
	const close = () => {
		if (!isPending) {
			onClose();
		}
	};

	return (
		<OverlayShell
			label="Resolver movimento bancário"
			onClose={close}
			className="items-center justify-center p-4"
		>
			<div className="overlay-panel max-w-xl overflow-hidden">
				<ResolutionPanel
					data={resolution.data}
					isLoading={resolution.isLoading}
					isError={resolution.isError}
					isPending={isPending}
					onReceipt={(receiptId, bankId) =>
						receipt.mutate({
							id: receiptId,
							bank_transaction_id: bankId,
						})
					}
					onBill={(billId, bankId) =>
						bill.mutate({
							id: billId,
							bank_transaction_id: bankId,
						})
					}
					onSettlement={(externalId, bankId) =>
						settlement.mutate({
							external_id: externalId,
							bank_transaction_id: bankId,
						})
					}
					onModal={onModal}
				/>
				<div className="flex justify-end border-t border-border p-3">
					<Button variant="ghost" disabled={isPending} onClick={close}>
						Fechar
					</Button>
				</div>
			</div>
		</OverlayShell>
	);
}
